import { z } from 'zod';

import type { Interrupt, InterruptResumeHint } from '@core/tellent-assistant-types';

export const INTERRUPT_SENTINEL_KEY = '__interrupt';

const agentResumeHintSchema = z.object({ kind: z.literal('agent') });
const workflowResumeHintSchema = z.object({
  kind: z.literal('workflow'),
  workflowId: z.string().min(1),
  runId: z.string().min(1),
  stepName: z.string().min(1),
});
const interruptResumeHintSchema = z.discriminatedUnion('kind', [
  agentResumeHintSchema,
  workflowResumeHintSchema,
]);

export const interruptSentinelBodySchema = z.object({
  id: z.string().min(1),
  reason: z.string().min(1),
  payload: z.unknown(),
  resume: interruptResumeHintSchema,
});

export type InterruptSentinelBody = z.infer<typeof interruptSentinelBodySchema>;

/**
 * Returns the sentinel body if `value` is an object with a valid
 * `__interrupt` property, otherwise null. Safe to call on any parsed JSON.
 */
export function extractInterruptSentinel(value: unknown): InterruptSentinelBody | null {
  if (!value || typeof value !== 'object') return null;
  const raw = (<Record<string, unknown>>value)[INTERRUPT_SENTINEL_KEY];
  if (!raw) return null;
  const res = interruptSentinelBodySchema.safeParse(raw);
  return res.success ? res.data : null;
}

/**
 * Converts a validated sentinel body into a store-ready Interrupt.
 */
export function sentinelToInterrupt(body: InterruptSentinelBody): Interrupt {
  return {
    id: body.id,
    reason: body.reason,
    payload: body.payload,
    resume: <InterruptResumeHint>body.resume,
  };
}
